import { z } from "zod";

// 우편번호 검증 정규식 (숫자만, 8자까지)
const zipRegex = /^[0-9]{1,8}$/;

// 이메일 검증 정규식
const emailRegex = /^[a-z0-9]([a-z0-9._-]*[a-z0-9])?@[a-z0-9]([a-z0-9.-]*[a-z0-9])?\.[a-z0-9]([a-z0-9.-]*[a-z0-9])?$/;

// 비밀번호: 8~50자, 영문(대/소문자), 숫자, 특수문자 각 1개 이상, 공백 불가
// eslint-disable-next-line no-useless-escape
const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[~`!@#$%&*()_+\-={}|[\]\\:";'<>?,.\/^])(?!.*\s).{8,50}$/;

// Factory function for email schema with translations
export const createEmailSchema = (t: (key: string) => string) => {
  return z
    .string()
    .min(1, t("ura_err_id_1"))
    .regex(emailRegex, t("ura_err_id_2"))
    .refine((email) => {
      const atIndex = email.indexOf("@");
      const dotIndex = email.indexOf(".", atIndex);
      return atIndex > 0 && dotIndex > atIndex;
    }, t("ura_err_id_2"))
    .refine((email) => {
      const localPart = email.split("@")[0];
      return !localPart.includes("..");
    }, t("ura_err_id_2"))
    .refine((email) => {
      const localPart = email.split("@")[0];
      return /^[a-z0-9].*[a-z0-9]$/.test(localPart) || localPart.length === 1;
    }, t("ura_err_id_2"));
};

// Password schema for signup forms
export const passwordSchema = z
  .string()
  .min(8, "비밀번호는 8자 이상의 영문 대/소문자, 숫자, 특수문자를 사용해주세요.")
  .max(50, "비밀번호는 50자 이하로 입력해주세요.")
  .regex(passwordRegex, "비밀번호는 8자 이상의 영문 대/소문자, 숫자, 특수문자를 사용해주세요.")
  .refine((val) => !/\s/.test(val), {
    message: "비밀번호는 공백을 포함할 수 없습니다.",
  });

// Password confirmation schema
export const confirmPasswordSchema = z.string().min(1, "비밀번호 확인을 입력해주세요.");

// Address schema factory function
export const createAddressSchema = (t: (key: string) => string) => {
  return z.object({
    zip: z.string().min(1, t("f_ei_err_ads")),
    streetAddress: z.string().min(1, t("f_ei_err_ads")),
    houseNumber: z.string().min(1, t("f_ei_err_ads")),
    complement: z.string().optional(),
    area: z.string().min(1, t("f_ei_err_ads")),
    city: z.string().min(1, t("f_ei_err_ads")),
    state: z.string().min(1, t("f_ei_err_ads")),
  });
};

// Fallback address schema (for backward compatibility)
export const addressSchema = z.object({
  zip: z.string().min(1, "우편번호를 입력해주세요.").regex(zipRegex, "우편번호는 숫자만 입력해주세요 (최대 8자)"),
  streetAddress: z.string().min(1),
  houseNumber: z.string().min(1),
  complement: z.string().optional(), //선택
  area: z.string().min(1),
  city: z.string().min(1),
  state: z.string().min(1, "병원 주소를 입력해 주세요."),
});

// Basic user profile schema (without passwords) - for admin user creation
export const userProfileSchema = z.object({
  email: createEmailSchema((key) => key),
  verificationCode: z.string().optional(),
  countryCode: z.string().min(1, "국가 코드를 입력해주세요."),
  phoneNumber: z.string().min(1, "연락처를 입력해주세요."),
  companyName: z.string().min(1, "병원명을 입력해주세요."),
  adminId: z.string().optional(), // 담당 관리자 ID (선택)
  adminName: z.string().optional(), // 담당 관리자 이름 (화면 표시용)
});

// Extended profile schema with address for complete user registration
export const userProfileWithAddressSchema = userProfileSchema.merge(addressSchema);

// Terms and conditions schema
export const termsSchema = z.object({
  agreeTerms: z.boolean().optional(),
  agreePrivacy: z.boolean().optional(),
  agreeMarketing: z.boolean().optional(),
});

// Factory function for complete signup schema
export const createSignupSchema = (t: (key: string) => string) => {
  return z.object({
    // Email field
    email: createEmailSchema(t),
    verificationCode: z.string().optional(),

    // Profile fields
    countryCode: z.string().min(1, t("f_ei_lable_nation")),
    phoneNumber: z.string().min(1, t("f_ei_placeholder_phone")),
    companyName: z.string().min(1, t("f_ei_placeholder_name")),
    adminId: z.string().optional(),
    adminName: z.string().optional(),

    // Address fields
    zip: z.string().min(1, t("f_ei_err_ads")),
    streetAddress: z.string().min(1, t("f_ei_err_ads")),
    houseNumber: z.string().min(1, t("f_ei_err_ads")),
    complement: z.string().optional(),
    area: z.string().min(1, t("f_ei_err_ads")),
    city: z.string().min(1, t("f_ei_err_ads")),
    state: z.string().min(1, t("f_ei_err_ads")),

    // Password
    password: z
      .string()
      .min(1, t("f_pw_placeholder_pw"))
      .min(8, t("f_pw_err_pw_1"))
      .regex(passwordRegex, t("f_pw_err_pw_1"))
      .refine((val) => !/\s/.test(val), { message: t("f_pw_placeholder_pw") }),

    // Terms
    agreeTerms: z.boolean().refine((val) => val === true, t("f_cu_err_agree")),
    agreePrivacy: z.boolean().refine((val) => val === true, t("f_cu_err_agree")),
    agreeMarketing: z.boolean().optional(),
  });
};

export const passwordResetSchema = z.object({
  email: createEmailSchema((key) => key),
  verificationCode: z.string().optional(),
  newPassword: passwordSchema,
});

export const createPasswordResetSchema = (t: (key: string) => string) => {
  return z.object({
    email: createEmailSchema(t),
    verificationCode: z.string().optional(),
    newPassword: z
      .string()
      .min(1, t("f_pw_placeholder_pw"))
      .min(8, t("f_pw_err_pw_1"))
      .regex(passwordRegex, t("f_pw_err_pw_1"))
      .refine((val) => !/\s/.test(val), { message: t("f_pw_placeholder_pw") }),
  });
};

// Admin user creation schema factory function
export const createAdminUserCreationSchema = (t: (key: string) => string) => {
  return z.object({
    // Email field
    email: createEmailSchema(t),

    // Profile fields
    countryCode: z.string().min(1, t("ura_err_nation")),
    phoneNumber: z.string().min(10, t("ura_err_phone")),
    companyName: z.string().min(1, t("ura_err_name")),
    adminId: z.string().optional(),
    adminName: z.string().optional(),

    // Address fields using factory
    ...createAddressSchema(t).shape,

    // Password (generated)
    password: z.string().optional(),
  });
};

// Fallback schema (for backward compatibility)
export const adminUserCreationSchema = userProfileWithAddressSchema.extend({
  password: z.string().optional(), // Generated password (optional in form, required in API)
});

// Type exports
export type UserProfile = z.infer<typeof userProfileSchema>;
export type UserProfileWithAddress = z.infer<typeof userProfileWithAddressSchema>;
export type SignupFormValues = z.infer<ReturnType<typeof createSignupSchema>>;
export type AdminUserCreationValues = z.infer<ReturnType<typeof createAdminUserCreationSchema>>;
export type PasswordResetFormValues = z.infer<ReturnType<typeof createPasswordResetSchema>>;
